<script setup lang="ts">
import Pdf from "@/components/Pdf.vue"
import VarsityInlineConfirm from "@/components/VarsityInlineConfirm.vue"
import VarsityInlineConfirmButton from "@/components/VarsityInlineConfirmButton.vue"
import VarsityInlineConfirmGroup from "@/components/VarsityInlineConfirmGroup.vue"
import ConditionForm from "@/components/form/ConditionForm.vue"
import { createInlineConfirm } from "@/composables/createInlineConfirm"
import type { JobActivity } from "@/model/staff/job-activity"
import type { LoginJobActivity } from "@/model/staff/login-job-activity"
import ResponseAttachments from "@/pages/Tasks/Issue/ResponseAttachments.vue"
import { appendFormDataToFormData } from "@/utils/formData"
import { useHttpClient } from "@/vf"
import { computed, reactive, ref } from "vue"
import { type TasksProps } from "../tasks-props"

const props = defineProps<TasksProps<LoginJobActivity>>()
const emit = defineEmits<{
    (e: "updated"): void
}>()

const http = useHttpClient()

const model = ref({
    attachments: [],
})

const inlineConfirmAccept = createInlineConfirm({
    async action() {
        const formData = new FormData()
        for (const condition of jobActivity.legalConditions) {
            const formElement = document.getElementById(condition.id) as HTMLFormElement

            if (!formElement) {
                // condition has no form
                continue
            }

            let conditionFormData = new FormData(formElement)
            conditionFormData = appendFormDataToFormData(
                formData,
                conditionFormData,
                "conditions[" + condition.id + "]",
            )
        }

        if (model?.value.attachments?.length > 0) {
            for (const file of model.value.attachments) {
                formData.append("attachments[]", file)
            }
        }

        await http.post(`/staff-admin/login-activity/${props.task.id}/accept`, formData)
    },
    afterAction() {
        emit("updated")
    },
})

const inlineConfirmDecline = createInlineConfirm({
    async action() {
        await http.post(`/staff-admin/login-activity/${props.task.id}/decline`, {})
    },
    afterAction() {
        emit("updated")
    },
})

const signatureFields = reactive<Record<string, { isEmpty: boolean; signature?: string; city?: string }>>({})

const hasEmptySignatureFields = computed(() => Object.values(signatureFields).some(i => i.isEmpty))

const jobActivity = (props.task as { jobActivity: JobActivity }).jobActivity
for (const condition of jobActivity.legalConditions) {
    signatureFields[condition.id] = { isEmpty: true }
}

// even though this isn't used, it's required to make the form work, because some values get calculated based on this
// object...
const data = ref({})
</script>

<template>
    <div class="p-4">
        <div v-if="viewMode === 'customer' && props.task.state === 'open'" class="mt-4">
            <template v-for="condition in jobActivity.legalConditions" :key="condition.id">
                <pdf :file="`/legal/form/pdf/staff/${props.task.id}/${condition.id}`"></pdf>
                <ConditionForm
                    v-model="data"
                    :form-ids="[condition.id]"
                    :form-id="condition.id"
                    @has-empty-signature-fields="signatureFields[condition.id].isEmpty = $event"
                ></ConditionForm>
            </template>
            <div class="form-group d-block" v-if="jobActivity.documentUploadRequired">
                <div v-html="jobActivity.documentUploadIntroText"></div>
                <label class="d-block font-weight-bold">{{ $t("@cms:contactForm.attachments") }}:</label>
                <ResponseAttachments v-model="model.attachments"></ResponseAttachments>
            </div>
            <VarsityInlineConfirmGroup>
                <VarsityInlineConfirmButton
                    class="btn btn-success btn-shape-skewed"
                    :controller="inlineConfirmAccept"
                    :disabled="
                        hasEmptySignatureFields ||
                        (jobActivity.documentUploadRequired && model.attachments.length === 0)
                    "
                >
                    {{ $t("@tasks:tasks.account_invitation.accept", { name: jobActivity.name }) }}
                </VarsityInlineConfirmButton>
                <VarsityInlineConfirmButton class="btn btn-danger btn-shape-skewed" :controller="inlineConfirmDecline">
                    {{ $t("@tasks:tasks.account_invitation.decline", { name: jobActivity.name }) }}
                </VarsityInlineConfirmButton>

                <VarsityInlineConfirm :controller="inlineConfirmAccept" :spinner="true">
                    <template #confirmation>{{ $t("@tasks:tasks.login_job_activity.accept_confirm") }}</template>
                    <template #success>{{ $t("@tasks:tasks.login_job_activity.accept_successfull") }}</template>
                </VarsityInlineConfirm>

                <VarsityInlineConfirm :controller="inlineConfirmDecline">
                    <template #confirmation>{{ $t("@tasks:tasks.login_job_activity.decline_confirm") }}</template>
                    <template #success>{{ $t("@tasks:tasks.login_job_activity.decline_successfull") }}</template>
                </VarsityInlineConfirm>
            </VarsityInlineConfirmGroup>
        </div>
    </div>
</template>
